import { DAILY_PATHS, LOCALES, SITE_LOCALES, SITE_PATHS, getLocaleConfig, href, localeFromPath, rashiFromSlug, signSlug, type Locale, type LocaleConfig } from '@/lib/i18n';
import { TOOL_SLUGS } from '@/content/tools';

export interface LanguageAlternates {
  /** Languages the current page exists in. */
  locales: LocaleConfig[];
  /** The current page's path in a given language. */
  pathFor: (l: Locale) => string;
}

/**
 * Which languages the page at `pathname` exists in, and its path in each.
 * Daily pages exist in every locale (sign slugs differ per language); site pages
 * in the `site: true` locales; everything else (blog, legal, the app) only in
 * English. Shared by the footer switcher and the navbar toggle so the two can
 * never disagree about where a language lives.
 */
export function languageAlternates(pathname: string): LanguageAlternates | null {
  const locale = localeFromPath(pathname);
  const prefix = getLocaleConfig(locale).prefix;
  const base = prefix && pathname.startsWith(prefix) ? pathname.slice(prefix.length) || '/' : pathname;
  const sign = base.match(/^\/rashifal\/([^/]+)$/);
  if (sign) {
    const rashi = rashiFromSlug(locale, sign[1]);
    if (!rashi) return null;
    return { locales: LOCALES, pathFor: (l) => href(l, `/rashifal/${signSlug(l, rashi)}`) };
  }
  if (DAILY_PATHS.includes(base)) return { locales: LOCALES, pathFor: (l) => href(l, base) };
  if (SITE_PATHS.includes(base) || TOOL_SLUGS.includes(base.slice(1))) {
    return { locales: SITE_LOCALES, pathFor: (l) => (base === '/' ? getLocaleConfig(l).prefix || '/' : href(l, base)) };
  }
  return null;
}

/**
 * Where the navbar toggle should send someone who picks `target` while on
 * `pathname`: the same page in that language when it exists there, otherwise
 * the closest thing that does — the localised homepage for a full-site
 * language, the daily rashifal hub for a daily-only one. Returns null when the
 * page is already in that language.
 */
export function languageTarget(pathname: string, target: Locale): string | null {
  if (localeFromPath(pathname) === target) return null;
  const alts = languageAlternates(pathname);
  if (alts && alts.locales.some((l) => l.code === target)) return alts.pathFor(target);
  const cfg = getLocaleConfig(target);
  if (cfg.site) return cfg.prefix || '/';
  return href(target, '/rashifal');
}
